import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const STATUS_LABEL = {
  pending_payment: 'Aguard. pagamento',
  waiting_delivery: 'Buscando entregador',
  accepted: 'Aceito',
  in_transit: 'A caminho',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
};
const STATUS_COLOR = {
  pending_payment: 'bg-yellow-100 text-yellow-700',
  waiting_delivery: 'bg-blue-100 text-blue-700',
  accepted: 'bg-indigo-100 text-indigo-700',
  in_transit: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    api.get('/admin/stats').then(r => setStats(r.data));
    api.get('/admin/orders').then(r => setOrders(r.data));
    api.get('/admin/users').then(r => setUsers(r.data));
    api.get('/admin/withdrawals').then(r => setWithdrawals(r.data));
  }, []);

  const refresh = () => {
    api.get('/admin/stats').then(r => setStats(r.data));
    api.get('/admin/orders').then(r => setOrders(r.data));
    api.get('/admin/users').then(r => setUsers(r.data));
    api.get('/admin/withdrawals').then(r => setWithdrawals(r.data));
  };

  const updateWithdrawalStatus = async (withdrawalId, status) => {
    await api.put(`/admin/withdrawals/${withdrawalId}`, { status });
    refresh();
  };

  const updateApproval = async (userId, approvalStatus) => {
    await api.put(`/admin/users/${userId}/approval`, { approvalStatus });
    refresh();
  };

  const deliveryUsers = users.filter(user => user.role === 'delivery');

  return (
    <div className="screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="gradient-hero px-6 pt-10 pb-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="GasNaPorta" className="h-20 w-auto object-contain logo-glow" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-black">Administrativo</span>
              <h1 className="text-base font-black mt-0.5">Painel de gestão</h1>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <button onClick={refresh} className="text-sky-200 hover:text-white text-sm">↺</button>
              <button onClick={() => nav('/admin/relatorios')} className="text-xs bg-emerald-500 hover:bg-emerald-400 px-3 py-1.5 rounded-full font-bold transition-colors">Relatórios</button>
              <button onClick={() => nav('/admin/precos')} className="text-xs btn-primary px-3 py-1.5 rounded-full font-bold">Preços</button>
              <button onClick={() => nav('/admin/usuarios')} className="text-xs bg-sky-600 hover:bg-sky-500 px-3 py-1.5 rounded-full font-bold transition-colors text-white">Usuários</button>
              <button onClick={logout} className="text-sky-200 hover:text-white text-sm">Sair</button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-gray-100">
        {[['overview', 'Visão Geral'], ['orders', 'Pedidos'], ['users', 'Usuários'], ['withdrawals', 'Saques']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex-1 py-3 text-xs font-bold transition-colors border-b-2 ${tab === id ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">

        {/* OVERVIEW */}
        {tab === 'overview' && stats && (
          <div className="space-y-3 fade-in">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Total pedidos', value: stats.totalOrders, icon: '📦', color: 'bg-blue-50 text-blue-700' },
                { label: 'Entregues', value: stats.deliveredOrders, icon: '✅', color: 'bg-green-50 text-green-700' },
                { label: 'Em andamento', value: stats.pendingOrders, icon: '⏳', color: 'bg-orange-50 text-orange-700' },
                { label: 'Entregadores online', value: stats.onlineDelivery, icon: '🛵', color: 'bg-indigo-50 text-indigo-700' },
              ].map(s => (
                <div key={s.label} className={`rounded-2xl p-4 ${s.color}`}>
                  <div className="text-2xl mb-1">{s.icon}</div>
                  <div className="font-black text-2xl">{s.value}</div>
                  <div className="text-xs opacity-70">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Revenue */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Financeiro</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Receita total (clientes)</span>
                  <span className="font-bold text-gray-800">R$ {stats.totalRevenue?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Repasse entregadores</span>
                  <span className="font-bold text-red-600">- R$ {stats.totalPayouts?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Gorjetas repassadas</span>
                  <span className="font-bold text-emerald-600">R$ {stats.totalTips?.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-bold text-gray-700">Lucro da plataforma</span>
                  <span className="font-black text-green-600 text-lg">R$ {stats.profit?.toFixed(2)}</span>
                </div>
                <div className="text-[11px] text-gray-400 pt-1">
                  Taxa atual da plataforma: {(stats.platformFeeRate * 100).toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Users */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Usuários</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center">
                  <div className="font-black text-2xl text-gray-800">{stats.totalCustomers}</div>
                  <div className="text-xs text-gray-500">Clientes</div>
                </div>
                <div className="text-center">
                  <div className="font-black text-2xl text-gray-800">{stats.totalDelivery}</div>
                  <div className="text-xs text-gray-500">Entregadores</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ORDERS */}
        {tab === 'orders' && (
          <div className="space-y-2 fade-in">
            {orders.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <p className="text-4xl mb-2">📦</p>
                <p>Nenhum pedido ainda</p>
              </div>
            )}
            {orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(o => {
              const st = STATUS_LABEL[o.status] || o.status;
              const sc = STATUS_COLOR[o.status] || 'bg-gray-100 text-gray-600';
              return (
                <div key={o.id} className="bg-white rounded-2xl p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span>{o.brandEmoji}</span>
                      <span className="font-bold text-sm text-gray-800">{o.brandName} {o.sizeLabel}</span>
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${sc}`}>{st}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-1">📍 {o.address}</div>
                  {o.status === 'delivered' && o.deliveryPerson && (
                    <div className="bg-gray-50 rounded-xl p-3 mb-2">
                      <div className="text-xs text-gray-500">Entregador que executou</div>
                      <div className="font-bold text-sm text-gray-800">{o.deliveryPerson.name}</div>
                      <div className="text-xs text-gray-500">
                        {o.deliveryPerson.vehicle} • ⭐ {o.deliveryPerson.rating?.toFixed?.(1) || o.deliveryPerson.rating}
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        Avaliação do cliente:{' '}
                        <span className="font-bold text-yellow-600">
                          {o.customerRating ? `${o.customerRating} estrela(s)` : 'Sem avaliação'}
                        </span>
                      </div>
                      {o.customerComment && (
                        <div className="text-xs text-gray-600 mt-2 bg-white rounded-lg p-2 border border-gray-100">
                          “{o.customerComment}”
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-4 text-xs">
                    <span className="text-gray-500">Cliente: <span className="text-primary font-bold">R$ {o.customerPrice?.toFixed(2)}</span></span>
                    <span className="text-gray-500">Entregador: <span className="text-green-600 font-bold">R$ {o.deliveryPrice?.toFixed(2)}</span></span>
                    <span className="text-gray-500 ml-auto">{new Date(o.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* USERS */}
        {tab === 'users' && (
          <div className="space-y-2 fade-in">
            {users.filter(u => u.role !== 'admin').map(u => (
              <div key={u.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${u.role === 'delivery' ? 'bg-orange-100' : 'bg-blue-100'}`}>
                  {u.role === 'delivery' ? '🛵' : '🛒'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-800 text-sm truncate">{u.name}</div>
                  <div className="text-xs text-gray-400">{u.email}</div>
                  {u.role === 'delivery' && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`w-2 h-2 rounded-full ${u.isOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
                      <span className="text-xs text-gray-400">{u.isOnline ? 'Online' : 'Offline'}</span>
                      {u.brands?.length > 0 && <span className="text-xs text-gray-400">• {u.brands.length} marca(s)</span>}
                    </div>
                  )}
                </div>
                {u.role === 'delivery' && (
                  <div className="text-right">
                    <div className="text-green-600 font-bold text-sm">R$ {u.balance?.toFixed(2)}</div>
                    <div className="text-xs text-gray-400">{u.deliveriesCount} entrega(s) • ⭐ {u.rating?.toFixed?.(1) || u.rating || 5}</div>
                  </div>
                )}
              </div>
            ))}
            {deliveryUsers.map(u => (
              <div key={`${u.id}-approval`} className="bg-amber-50 border border-amber-100 rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-black text-gray-800 text-sm">{u.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Status do cadastro: <span className="font-bold">
                        {u.approvalStatus === 'approved' ? 'Aprovado' : u.approvalStatus === 'rejected' ? 'Reprovado' : 'Pendente'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{u.approvalMessage}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => updateApproval(u.id, 'approved')}
                      className="px-3 py-2 rounded-xl bg-green-600 text-white text-xs font-bold"
                    >
                      Aprovar
                    </button>
                    <button
                      onClick={() => updateApproval(u.id, 'pending')}
                      className="px-3 py-2 rounded-xl bg-yellow-100 text-yellow-700 text-xs font-bold"
                    >
                      Pendente
                    </button>
                    <button
                      onClick={() => updateApproval(u.id, 'rejected')}
                      className="px-3 py-2 rounded-xl bg-red-100 text-red-700 text-xs font-bold"
                    >
                      Reprovar
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <div className="pt-2">
              <h3 className="font-black text-gray-800 text-sm mb-3">Documentos do entregador</h3>
              <div className="space-y-3">
                {deliveryUsers.map(user => (
                  <div key={`${user.id}-docs`} className="bg-white rounded-2xl p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="font-black text-gray-800">{user.name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          PIX CPF: <span className="font-semibold text-gray-700">{user.pixKeyCpf || '—'}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          ANP: <span className="font-semibold text-gray-700">{user.anpCode || '—'}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Veículo: <span className="font-semibold text-gray-700">{user.vehicleModel || user.vehicle || '—'}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Placa: <span className="font-semibold text-gray-700">{user.vehiclePlate || '—'}</span>
                        </div>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                        user.approvalStatus === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : user.approvalStatus === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {user.approvalStatus === 'approved' ? 'Aprovado' : user.approvalStatus === 'rejected' ? 'Reprovado' : 'Pendente'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs font-bold text-gray-500 mb-2">CNH frente</div>
                        {user.cnhFrontImage ? (
                          <img
                            src={user.cnhFrontImage}
                            alt={`CNH frente ${user.name}`}
                            className="w-full h-36 object-cover rounded-xl border border-gray-200 bg-gray-50"
                          />
                        ) : (
                          <div className="w-full h-36 rounded-xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-xs text-gray-400">
                            Não enviado
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-500 mb-2">CNH verso</div>
                        {user.cnhBackImage ? (
                          <img
                            src={user.cnhBackImage}
                            alt={`CNH verso ${user.name}`}
                            className="w-full h-36 object-cover rounded-xl border border-gray-200 bg-gray-50"
                          />
                        ) : (
                          <div className="w-full h-36 rounded-xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-xs text-gray-400">
                            Não enviado
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* WITHDRAWALS */}
        {tab === 'withdrawals' && (
          <div className="space-y-2 fade-in">
            {withdrawals.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <p className="text-4xl mb-2">💸</p>
                <p>Nenhuma solicitação de saque ainda</p>
              </div>
            )}
            {withdrawals.map(item => (
              <div key={item.id} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-bold text-gray-800 text-sm">{item.deliveryName}</div>
                    <div className="text-xs text-gray-400">{item.deliveryPhone}</div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    item.status === 'paid' ? 'bg-green-100 text-green-700' :
                    item.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {item.status === 'paid' ? 'Pago' : item.status === 'cancelled' ? 'Cancelado' : 'Pendente'}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Valor</span>
                  <span className="font-black text-green-600">R$ {item.amount.toFixed(2)}</span>
                </div>
                <div className="text-xs text-gray-500">Solicitado em {new Date(item.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                {item.estimatedPaymentAt && (
                  <div className="text-xs text-gray-500 mt-1">Pagamento até {new Date(item.estimatedPaymentAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                )}
                {item.status === 'requested' && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => updateWithdrawalStatus(item.id, 'paid')}
                      className="flex-1 py-2 rounded-xl bg-green-600 text-white text-sm font-bold"
                    >
                      Marcar como pago
                    </button>
                    <button
                      onClick={() => updateWithdrawalStatus(item.id, 'cancelled')}
                      className="flex-1 py-2 rounded-xl bg-red-100 text-red-700 text-sm font-bold"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
